import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { PlatformPreviewProps } from '../types';
import { PLATFORM_CONFIG, truncateForPlatform } from '../platformConfig';
import { PreviewAvatar } from '../components/PreviewAvatar';
import { PreviewCaption } from '../components/PreviewCaption';
import { MediaDisplay } from '../components/MediaDisplay';
import { EmptyState } from '../components/EmptyState';
import { DeviceFrame } from '../components/DeviceFrame';

export const InstagramPreview = ({ caption, media, isCarousel, channel, activeIndex, onIndexChange }: PlatformPreviewProps) => {
  const { t } = useTranslation('common');
  const config = PLATFORM_CONFIG.instagram;
  const displayCaption = truncateForPlatform(caption, 'instagram');
  const isEmpty = !caption && media.length === 0;

  const handle = channel?.handle ?? '';
  const options = channel?.options;
  const contentType = options?.instagram?.contentType || 'FEED';

  if (isEmpty) return <EmptyState />;

  const displayMedia = isCarousel ? media.slice(0, 10) : media.slice(0, 1);

  // Reels/Stories use 9:16, Feed uses 1:1
  const isVertical = contentType === 'REELS' || contentType === 'STORIES';

  return (
    <DeviceFrame variant={isVertical ? 'phone' : 'card'}>
      <div className="bg-white">
        {/* Header */}
        <div className="px-3 py-2.5 flex items-center gap-2.5">
          <PreviewAvatar colorClass={config.avatarColor} size="sm" ring={contentType === 'STORIES'} avatarUrl={channel?.avatarUrl} initials={channel?.initials} />
          <span className="font-semibold text-[14px] flex-1 truncate">{handle}</span>
          {/* Content type badge */}
          {contentType !== 'FEED' && (
            <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              {contentType === 'REELS' ? t('preview_instagram_reel') : t('preview_instagram_story')}
            </span>
          )}
          <span className="text-gray-900 text-base leading-none tracking-widest" aria-hidden="true">
            ···
          </span>
        </div>

        {/* Media */}
        {displayMedia.length > 0 ? (
          <div className="relative">
            <MediaDisplay
              media={displayMedia}
              aspectRatio={isVertical ? 'portrait' : 'square'}
              rounded="none"
              showCarouselDots={isCarousel && displayMedia.length > 1}
              activeIndex={activeIndex}
              onIndexChange={onIndexChange}
            />
            {/* Reels icon overlay */}
            {contentType === 'REELS' && (
              <div className="absolute top-3 right-3 bg-black/50 rounded-full p-1.5">
                <Icon name={ICONS.VIDEO} size="xs" color="text-white" />
              </div>
            )}
          </div>
        ) : (
          <div className={isVertical ? 'aspect-[9/16]' : 'aspect-square'} style={{ background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name={ICONS.IMAGE} size="xl" color="text-gray-300" />
          </div>
        )}

        {/* Actions (not shown for Stories) */}
        {contentType !== 'STORIES' && (
          <>
            <div className="px-3 pt-2.5 pb-1.5 flex items-center gap-[14px]">
              <Icon name={ICONS.HEART} size="custom" className="text-[22px]" color="text-gray-900" />
              <Icon name={ICONS.CHAT} size="custom" className="text-[22px]" color="text-gray-900" />
              <Icon name={ICONS.PAPER_PLANE} size="custom" className="text-[22px]" color="text-gray-900" />
              <span className="ml-auto inline-flex">
                <Icon name={ICONS.BOOKMARK} size="custom" className="text-[22px]" color="text-gray-900" />
              </span>
            </div>

            {/* Caption */}
            {caption && (
              <div className="px-3 pb-3">
                <PreviewCaption
                  caption={displayCaption}
                  hashtagColor={config.hashtagColor}
                  className="text-[length:var(--mock-caption-size,14px)] leading-[1.4] text-gray-900"
                  prefix={<span className="font-semibold">{handle}</span>}
                  truncated={caption.length > 150}
                />
              </div>
            )}
          </>
        )}
      </div>
    </DeviceFrame>
  );
};
